import { useRef, useCallback, ReactNode, useState, useEffect } from 'react'
import { VariableSizeList, ListChildComponentProps } from 'react-window'
import styled from 'styled-components'

const ListContainer = styled.div`
  flex: 1;
  min-height: 0;
  margin-top: 20px;
`

const CARD_GAP = 12 // Gap between cards in pixels

const CardWrapper = styled.div`
  padding-bottom: ${CARD_GAP}px;
`

interface VirtualizedCardListProps<T> {
  data: T[]
  renderCard: (item: T, index: number) => ReactNode
  getItemKey: (item: T) => string | number
  estimatedItemHeight?: number
  /** Map of item IDs to their expanded state */
  expandedItems?: Set<string | number>
  /** Height of the list container (default: auto-calculated) */
  height?: number
}

/**
 * Virtualized list for DataTable cards.
 * Uses react-window's VariableSizeList to handle expandable cards.
 */
export function VirtualizedCardList<T>({
  data,
  renderCard,
  getItemKey,
  estimatedItemHeight = 120,
  expandedItems,
  height: containerHeight,
}: VirtualizedCardListProps<T>) {
  const listRef = useRef<VariableSizeList>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemHeightsRef = useRef<Map<number, number>>(new Map())
  const [listHeight, setListHeight] = useState(containerHeight ?? 400)

  // Calculate container height
  useEffect(() => {
    if (containerHeight) {
      setListHeight(containerHeight)
      return
    }

    const container = containerRef.current
    if (!container) return

    const updateHeight = () => {
      const rect = container.getBoundingClientRect()
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      // Reserve space for pagination bar below the card list
      const bottomOffset = 56
      const availableHeight = viewportHeight - rect.top - bottomOffset
      setListHeight(Math.max(availableHeight, 300))
    }

    updateHeight()
    window.addEventListener('resize', updateHeight)
    window.visualViewport?.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateHeight)
      window.visualViewport?.removeEventListener('resize', updateHeight)
    }
  }, [containerHeight])

  // Get item size (height) - includes gap
  const getItemSize = useCallback(
    (index: number) => {
      // Return cached height if available (already includes gap)
      const cached = itemHeightsRef.current.get(index)
      if (cached) return cached

      // Estimate height - expanded items are taller
      const item = data[index]
      const itemKey = getItemKey(item)
      const isExpanded = expandedItems?.has(itemKey)

      // Base height + extra for expanded content + gap
      const baseHeight = isExpanded
        ? estimatedItemHeight * 2.5
        : estimatedItemHeight
      return baseHeight + CARD_GAP
    },
    [data, getItemKey, expandedItems, estimatedItemHeight]
  )

  // Update item height after render (includes gap)
  const setItemHeight = useCallback((index: number, height: number) => {
    const heightWithGap = height + CARD_GAP
    const currentHeight = itemHeightsRef.current.get(index)
    if (currentHeight !== heightWithGap) {
      itemHeightsRef.current.set(index, heightWithGap)
      listRef.current?.resetAfterIndex(index)
    }
  }, [])

  // Reset heights when expanded items change
  useEffect(() => {
    itemHeightsRef.current.clear()
    listRef.current?.resetAfterIndex(0)
  }, [expandedItems])

  // Row renderer
  const Row = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const item = data[index]
      const rowRef = useRef<HTMLDivElement>(null)

      // Measure row height after render (without gap, gap is added in setItemHeight)
      useEffect(() => {
        if (rowRef.current) {
          const height = rowRef.current.getBoundingClientRect().height
          setItemHeight(index, height)
        }
      })

      return (
        <div style={style}>
          <CardWrapper ref={rowRef}>{renderCard(item, index)}</CardWrapper>
        </div>
      )
    },
    [data, renderCard, setItemHeight]
  )

  // Item key getter
  const itemKey = useCallback(
    (index: number, itemData: T[]) => {
      return getItemKey(itemData[index])
    },
    [getItemKey]
  )

  return (
    <ListContainer ref={containerRef}>
      <VariableSizeList
        ref={listRef}
        height={listHeight}
        width="100%"
        itemCount={data.length}
        itemSize={getItemSize}
        itemData={data}
        itemKey={itemKey}
        overscanCount={5}
      >
        {Row}
      </VariableSizeList>
    </ListContainer>
  )
}

export default VirtualizedCardList
